import { captureException } from '@sentry/react'

import { getWebPushToken } from '@/core/firebase'

import {
  currentRevocation,
  registerNotificationHubDevice,
  removeNotificationHubDevice,
} from './notificationHubDevice'

interface RegisterOptions {
  requestPermission?: boolean
  getUserToken: () => Promise<string>
  locale?: string
}

/** The end-user token is minted only once a browser token exists. */
export const registerCurrentBrowserFcmToken = async ({
  requestPermission = false,
  getUserToken,
  locale,
}: RegisterOptions): Promise<void> => {
  try {
    // Before any await, so a logout during the fetches below is visible.
    const revocationAtStart = currentRevocation()

    const fcmToken = await getWebPushToken({ requestPermission })
    if (!fcmToken) {
      return
    }

    await registerNotificationHubDevice({
      userToken: await getUserToken(),
      fcmToken,
      locale,
      revocationAtStart,
    })
  } catch (error) {
    // Degraded rather than broken: nobody asked for push, so this must not
    // surface. Reported so a registration that stops working is still visible.
    captureException(error, {
      level: 'warning',
      tags: { section: 'web-push' },
    })
  }
}

export const removeCurrentBrowserFcmToken = async (): Promise<void> => {
  try {
    await removeNotificationHubDevice()
  } catch (error) {
    // A device left registered means a shared browser keeps receiving the
    // previous person's notifications, so this one is worth an error.
    captureException(error, {
      level: 'error',
      tags: { section: 'web-push' },
    })
  }
}
